/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'clsx';
import { useFormik } from 'formik';
import { omit } from 'lodash';
import React, { ChangeEvent, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  OutlinedInput,
} from '@mui/material';

import { HUModal2 } from '../../../components/HUModal2/HUModal2';
import { HUSpin } from '../../../components/HUSpin/HUSpin';
import { SimpleErrorModal } from '../../../components/SimpleErrorModal/SimpleErrorModal';
import { Namespaces } from '../../../i18n';
import { SuperuserSupportPlanApiService } from '../../../services/SuperuserSupportPlanApiService';
import { useFormStyles } from '../../../styles/form';
import { ApiClientError } from '../../../types/api/ApiClientError';
import { SupportPlanRequest, SupportPlanResponse } from '../../../types/common/PlanSupport';
import { useApiValidation } from '../../../utils/useApiValidation';
import { useObjectState } from '../../../utils/useObjectState';
import { useValidationTranslation } from '../../../utils/useValidationTranslation';
import { ModalType } from '../AdminManageSupportPlans.types';
import { initialValues, ManageSupportModalProps, validationSchema } from './ManageSupportPlanModal.types';

export const ManageSupportPlanModal = memo<ManageSupportModalProps>(({ type, supportPlan, onClose, onSuccess }) => {
  const [errorModal, setErrorModal] = useObjectState<{
    isOpen: boolean;
    error: string;
  }>({ isOpen: false, error: '' });

  const formClasses = useFormStyles();
  const { t } = useTranslation([Namespaces.Admin]);
  const { vtr } = useValidationTranslation();

  const { setApiError, getFieldApiError, resetFieldApiError } = useApiValidation();

  const handleError = useCallback(
    (error: ApiClientError) => {
      const validation = setApiError(error);
      if (validation.errorType === 'unexpected' || validation.errorType === 'common') {
        setErrorModal({
          isOpen: true,
          error: `manage_plans.dialogs.errors.${validation.error ?? ''}`,
        });
      }
    },
    [setErrorModal, setApiError],
  );

  const [createSupportPlan, { isLoading: isCreateLoading }] = useMutation(
    SuperuserSupportPlanApiService.createSupportPlan,
    {
      onSuccess,
      onError: (error: ApiClientError) => handleError(error),
    },
  );

  const [editSupportPlan, { isLoading: isEditLoading }] = useMutation(SuperuserSupportPlanApiService.editSupportPlan, {
    onSuccess,
    onError: (error: ApiClientError) => handleError(error),
  });

  const isProcessing = useMemo(() => isCreateLoading || isEditLoading, [isCreateLoading, isEditLoading]);

  const modalTitle = useMemo(
    () =>
      type === ModalType.CREATE
        ? t('manage_plans.dialogs.titles.support_plan.create')
        : t('manage_plans.dialogs.titles.support_plan.update'),
    [t, type],
  );

  const initialFormValues = useMemo<SupportPlanRequest | SupportPlanResponse>(
    () => ({
      ...(supportPlan ?? initialValues),
    }),
    [supportPlan],
  );

  const onFormSubmit = useCallback(
    async (values: SupportPlanRequest) =>
      supportPlan
        ? await editSupportPlan({
            data: omit(values, 'id', 'tableData'),
            planId: supportPlan.id,
          })
        : await createSupportPlan({ data: values }),
    [createSupportPlan, editSupportPlan, supportPlan],
  );

  const { values, touched, errors, handleBlur, handleChange, handleSubmit, submitForm, dirty } =
    useFormik<SupportPlanRequest>({
      validationSchema,
      enableReinitialize: true,
      initialValues: initialFormValues,
      onSubmit: onFormSubmit,
    });

  const getError = (field: keyof SupportPlanRequest): string => {
    return errors[field] || getFieldApiError(field) || '';
  };

  const hasError = (field: keyof SupportPlanRequest): boolean => {
    return !!touched[field] && !!getError(field);
  };

  const onTextFieldChange = (name: keyof SupportPlanRequest, event: ChangeEvent) => {
    handleChange(event);
    resetFieldApiError(name);
  };

  const getLabelKey = (key: string): string => {
    return `manage_plans.dialogs.labels.${key}`;
  };

  const onOk = () => setErrorModal({ isOpen: false, error: '' });

  return (
    <HUModal2
      open
      okButtonText={t('manage_plans.dialogs.buttons.submit')}
      okButtonProps={{ disabled: !dirty || isProcessing }}
      cancelButtonText={t('manage_plans.dialogs.buttons.cancel')}
      onCancel={onClose}
      onClose={onClose}
      disableRestoreFocus
      onOk={submitForm}
      size="big"
      title={modalTitle}
    >
      <HUSpin spinning={isProcessing} delay={50}>
        <form noValidate onSubmit={handleSubmit}>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('name')}
            variant="outlined"
          >
            <InputLabel htmlFor="name">{t(getLabelKey('name'))}</InputLabel>
            <OutlinedInput
              id="name"
              name="name"
              label={t(getLabelKey('name'))}
              error={hasError('name')}
              fullWidth
              type="text"
              value={values.name}
              onChange={onTextFieldChange.bind(null, 'name')}
              disabled={isProcessing}
              onBlur={handleBlur}
            />
            {hasError('name') && (
              <FormHelperText>
                {vtr(getError('name'), {
                  field: t(getLabelKey('name')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('description')}
            variant="outlined"
          >
            <InputLabel htmlFor="description">{t(getLabelKey('description'))}</InputLabel>
            <OutlinedInput
              id="description"
              name="description"
              label={t(getLabelKey('description'))}
              error={hasError('description')}
              fullWidth
              type="text"
              value={values.description}
              onChange={onTextFieldChange.bind(null, 'description')}
              disabled={isProcessing}
              onBlur={handleBlur}
            />
            {hasError('description') && (
              <FormHelperText>
                {vtr(getError('description'), {
                  field: t(getLabelKey('description')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('price')}
            variant="outlined"
          >
            <InputLabel htmlFor="price">{t(getLabelKey('price'))}</InputLabel>
            <OutlinedInput
              id="price"
              name="price"
              label={t(getLabelKey('price'))}
              error={hasError('price')}
              fullWidth
              type="number"
              value={values.price}
              onChange={onTextFieldChange.bind(null, 'price')}
              disabled={isProcessing}
              onBlur={handleBlur}
            />
            {hasError('price') && (
              <FormHelperText>
                {vtr(getError('price'), {
                  field: t(getLabelKey('price')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <div className={formClasses.checkboxes}>
            <FormControlLabel
              name="isDefault"
              checked={values.isDefault}
              onChange={handleChange}
              control={<Checkbox color="primary" />}
              label={t(getLabelKey('is_default'))}
              labelPlacement="end"
              disabled={isProcessing}
            />
          </div>
          <div className={formClasses.hiddenSubmitBtn}>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="medium"
              disabled={isProcessing || dirty}
              aria-label={t('manage_plans.dialogs.buttons.submit')}
            >
              &nbsp;
            </Button>
          </div>
          <SimpleErrorModal open={errorModal.isOpen} message={errorModal.error} t={t} onOk={onOk} />
        </form>
      </HUSpin>
    </HUModal2>
  );
});
